import { ReactElement, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import * as _ from 'lodash';
import { CommonButton } from 'gemlux-common-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { RootState } from '../../../redux/store';
import LockIconSvg from '../../../assets/img/lock-icon.svg';
import { theme } from '../../../theme/theme';
import useEnv from '../../../utils/use-env-hook';
import CustomModal from '../CustomModal/CustomModal';
import { gemActions, IGem } from '../../../redux/gem/gemSlice';
import { AiStatus } from '../../../utils/enums/gem-ai-status.enum';

function EditGemConfirmationModel(): ReactElement {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const gemToOpen = useAppSelector((state: RootState) => state.gem.gemToOpen);
  const lockedGemId = useAppSelector((state: RootState) => state.gem.lockedGemId);
  const componentStatus = useAppSelector((state: RootState) => state.gem.componentStatus);
  const isModelOpen = _.get(componentStatus, 'isEditGemConfirmationModelOpen', false) as boolean;
  const isCheckGemLockStatusLoading = _.get(componentStatus, 'isCheckGemLockStatusLoading', false) as boolean;
  const { baseUrl } = useEnv();
  const pathParts = location.pathname.split('/');
  const firstPathPart = `/${pathParts[1]}`;

  const handleEditGem = (gem: IGem) => {
    dispatch(gemActions.setComponentStatus({ isEditGemConfirmationModelOpen: false }));
    dispatch(gemActions.openEditGemModel({ gem }));
  };

  const handleFullDetail = () => {
    dispatch(gemActions.setComponentStatus({ isEditGemConfirmationModelOpen: false }));
    navigate(`${firstPathPart}/${gemToOpen?.gemId}`);
    dispatch(gemActions.resetLockedGem());
  };

  useEffect(() => {
    if (!isModelOpen) {
      dispatch(gemActions.resetLockedGem());
    }
  }, [isModelOpen]);

  useEffect(() => {
    if (gemToOpen && isModelOpen) {
      dispatch(
        gemActions.checkGemLockStatus({
          gemId: gemToOpen.gemId,
        }),
      );
    }
  }, [gemToOpen, isModelOpen]);

  return (
    <CustomModal
      modal
      open={isModelOpen}
      handleClose={() => dispatch(gemActions.setComponentStatus({ isEditGemConfirmationModelOpen: false }))}
      Icon={<Box component="img" src={LockIconSvg} />}
      title="Are you sure you want to edit the following gem record?"
    >
      <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column">
        <Box sx={{ display: 'flex', justifyContent: 'space-around', width: '100%' }} marginBottom="20px">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }} flexDirection="row">
            <Box
              component="img"
              src={`${baseUrl}/${gemToOpen?.appearanceImage}`}
              alt="gem image"
              sx={{
                width: '24px',
                height: '24px',
                objectFit: 'cover',
                borderRadius: 'full',
              }}
            />
            <Typography variant="tableheadBold" textAlign="center">
              {gemToOpen?.species}
            </Typography>
            <Typography variant="tableheadBold" textAlign="center">
              {gemToOpen?.color}
            </Typography>
            <Typography variant="tableheadBold" textAlign="center">
              {gemToOpen?.shape}
            </Typography>
            <Typography variant="tableheadBold" textAlign="center">
              {gemToOpen?.weight}ct
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" flexDirection="column" gap={2}>
          <Typography variant="textBase" style={{ textAlign: 'center', marginBottom: 6, color: '#525E6F' }}>
            Changes made to the gemstone profile will be overwritten in the GemPASSPORT and/or GemPASSPORT+ if already
            generated. Please download the latest version before proceeding.
          </Typography>
        </Box>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          mt={2}
          gap={2}
          paddingTop="30px"
          sx={{ width: '100%' }}
        >
          <CommonButton
            disabled={
              gemToOpen?.aiStatus === AiStatus.PENDING ||
              gemToOpen?.aiStatus === AiStatus.IN_PROGRESS ||
              lockedGemId === gemToOpen?.gemId
            }
            variant="contained"
            sx={{ width: '100%', fontSize: 15, fontWeight: 500, fontFamily: 'Inter', borderRadius: '8px' }}
            backgroundColor="#3348FF"
            padding="8px 12px"
            onClick={() => gemToOpen && handleEditGem(gemToOpen)}
            loading={isCheckGemLockStatusLoading}
          >
            Proceed to Edit
          </CommonButton>
          <CommonButton
            variant="outlined"
            data-testid="cancel-button"
            sx={{
              width: '100%',
              fontSize: 15,
              fontWeight: 500,
              fontFamily: 'Inter',
              color: '#3348FF',
              borderColor: '#3348FF',
              borderRadius: '8px',
              '&.Mui-disabled': {
                color: theme.palette.secondary.main,
                borderColor: theme.palette.secondary.main,
                backgroundColor: 'transparent',
                opacity: 1,
              },
              padding: '8px 12px',
            }}
            onClick={handleFullDetail}
          >
            Proceed to Download
          </CommonButton>
        </Box>
      </Box>
    </CustomModal>
  );
}

export default EditGemConfirmationModel;
